import { createAsyncThunk } from '@reduxjs/toolkit';

import { api } from '@/features/Analytics/api';
import { TimeseriesParams } from '@/features/Analytics/interfaces';
import {
  toNivoHeatmap,
  toNivoRadar,
  toNivoTimeseries,
} from '@/features/Analytics/utils/transformChartData';

import { getActionPrefix } from '@/utils/helpers';

const actionPrefix = getActionPrefix('analytics');

export const fetchTimeseries = createAsyncThunk(
  `${actionPrefix}/fetchTimeseries`,
  async (
    params: {
      focusId: number;
      groupBy: string;
      granularity: string;
      topN?: number;
      includeOther?: boolean;
    },
    { signal, rejectWithValue }
  ) => {
    const apiParams: TimeseriesParams = {
      group_by: params.groupBy,
      granularity: params.granularity,
    };

    if (params.topN !== undefined) apiParams.top_n = params.topN;

    if (params.includeOther !== undefined) apiParams.include_other = params.includeOther;

    try {
      const { data } = await api.getTimeseries(params.focusId, apiParams, signal);

      return toNivoTimeseries(data);
    } catch (error) {
      if (signal.aborted) return rejectWithValue(undefined);

      throw error;
    }
  }
);

export const fetchModalityRadar = createAsyncThunk(
  `${actionPrefix}/fetchModalityRadar`,
  async (params: { focusId: number }, { signal, rejectWithValue }) => {
    try {
      const { data } = await api.getModalityRadar(params.focusId, signal);

      return toNivoRadar(data);
    } catch (error) {
      if (signal.aborted) return rejectWithValue(undefined);

      throw error;
    }
  }
);

export const fetchPreciseHeatmap = createAsyncThunk(
  `${actionPrefix}/fetchPreciseHeatmap`,
  async (params: { focusId: number; subCategoryId: number }, { signal, rejectWithValue }) => {
    try {
      const { data } = await api.getPreciseHeatmap(params.focusId, params.subCategoryId, signal);

      return toNivoHeatmap(data);
    } catch (error) {
      if (signal.aborted) return rejectWithValue(undefined);

      throw error;
    }
  }
);

export const fetchVolumeByCountry = createAsyncThunk(
  `${actionPrefix}/fetchVolumeByCountry`,
  async (
    params: { focusId: number; granularity: string; subCategoryId?: number },
    { signal, rejectWithValue }
  ) => {
    const apiParams: TimeseriesParams = {
      group_by: 'country',
      granularity: params.granularity,
      top_n: 10,
      include_other: true,
    };

    if (params.subCategoryId !== undefined) apiParams.sub_category_id = params.subCategoryId;

    try {
      const { data } = await api.getVolumeByCountry(params.focusId, apiParams, signal);

      return toNivoTimeseries(data);
    } catch (error) {
      if (signal.aborted) return rejectWithValue(undefined);

      throw error;
    }
  }
);

export const fetchHeatmapSubcategories = createAsyncThunk(
  `${actionPrefix}/fetchHeatmapSubcategories`,
  async (params: { focusId: number }, { signal, rejectWithValue }) => {
    try {
      const { data } = await api.getHeatmapSubcategories(params.focusId, signal);

      return data;
    } catch (error) {
      if (signal.aborted) return rejectWithValue(undefined);

      throw error;
    }
  }
);

export const fetchCategoryCountry = createAsyncThunk(
  `${actionPrefix}/fetchCategoryCountry`,
  async (params: { focusId: number; granularity: string }, { signal, rejectWithValue }) => {
    try {
      const { data } = await api.getCategoryCountry(
        params.focusId,
        {
          top_n: 10,
          include_other: true,
          granularity: params.granularity,
        },
        signal
      );

      return data;
    } catch (error) {
      if (signal.aborted) return rejectWithValue(undefined);

      throw error;
    }
  }
);
